<template>
    <div class="max-w-[650px]">
        <prime-button
            class="!p-0"
            link
            label="Redeem a Gift Card"
            icon="pi pi-angle-right"
            icon-pos="right"
            :disabled="isProcessing"
            @click="handleToggleInput"
        />

        <div
            v-if="showCodeInput"
            class="py-2"
        >
            <div class="flex">
                <input-text
                    ref="codeInputRef"
                    v-model="giftCardInputValue"
                    placeholder="Gift Card Code"
                    class="w-full"
                    type="text"
                    :disabled="isProcessing"
                    :pt="{
                        root: {
                            class: errorMessage ? 'border-2 border-red-500' : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                    @keydown.enter.prevent="handleApplyCode"
                />

                <prime-button
                    label="Apply"
                    :loading="isLoading"
                    :disabled="isProcessing"
                    @click="handleApplyCode"
                />
            </div>

            <div class="text-red-600 pt-1 text-sm font-medium">
                {{ displayedErrorMessage || '&nbsp;' }}
            </div>
        </div>

        <gift-card-payment-form-item
            v-for="(giftCard, giftCardIndex) in giftCards"
            :key="giftCard.token"
            :gift-card="giftCard"
            @remove="handleRemoveGiftCard(giftCardIndex)"
        />
    </div>
</template>

<script setup lang="ts">
import { graphql } from '~/resources/graphql'
import InputText from 'primevue/inputtext'
import PrimeButton from 'primevue/button'
import { computed, nextTick, ref, watch } from 'vue'
import { useRpQuery } from '~/composables/graphql'
import { Decimal } from '~/composables/support/decimal'
import type { GiftCardPaymentInfo } from '~/components/Checkout/Sections/PaymentInformation.vue'
import { useToast } from 'primevue/usetoast'
import { useField } from 'vee-validate'
import GiftCardPaymentFormItem from '~/components/Checkout/Sections/Payment/GiftCardPaymentFormItem.vue'
import { useUpdateCheckoutGiftCardData } from '~/components/Checkout/Sections/Payment/checkout-payment-data'
import { useCheckoutFormProcessing } from '~/components/Checkout/Forms/use-checkout-form'

const props = defineProps<{
    checkoutTotal: Decimal
    paymentSourceId: string
}>()

const { isProcessing } = useCheckoutFormProcessing()

const { value: giftCardInputValue, errorMessage: errorGiftCardInputValue } =
    useField<string>('payment.giftCardInputValue', '', {
        validateOnValueUpdate: false
    })

const { value: giftCards } = useField<GiftCardPaymentInfo[]>(
    'payment.giftCards',
    '',
    {
        validateOnValueUpdate: false
    }
)

const codeInputRef = ref<InstanceType<typeof InputText>>()
const errorMessage = ref('')
const displayedErrorMessage = computed(() => {
    if (
        errorGiftCardInputValue.value !== undefined &&
        errorGiftCardInputValue.value !== ''
    ) {
        return errorGiftCardInputValue
    }

    return errorMessage
})

const isLoading = ref(false)
const toast = useToast()
const query = useRpQuery()
async function handleApplyCode() {
    // Blank code
    if (giftCardInputValue.value === '') {
        errorMessage.value = 'Please enter a code.'
        codeInputRef.value?.$el.select()
        return
    }

    isLoading.value = true
    const data = await query(
        graphql(/* GraphQL */ `
            query checkoutGiftCardQuery($code: String!) {
                giftCard(code: $code) {
                    __typename

                    ... on GiftCard {
                        currentBalance
                        code
                    }

                    ... on GiftCardLookupError {
                        message
                    }
                }
            }
        `),
        {
            code: giftCardInputValue.value
        }
    ).finally(() => {
        isLoading.value = false
    })

    if (data.giftCard.__typename === 'GiftCardLookupError') {
        errorMessage.value = data.giftCard.message
        codeInputRef.value?.$el.select()
        return
    }

    // If trying to add an empty gift card, return
    const giftCardCurrentBalance = new Decimal(data.giftCard.currentBalance)
    if (giftCardCurrentBalance.isZero()) {
        errorMessage.value =
            'This gift card is empty. No remaining balance left.'
        codeInputRef.value?.$el.select()
        return
    }

    // If gift card that has already been added, return
    const newCode = data.giftCard.code
    if (giftCards.value.some(card => card.token === newCode)) {
        errorMessage.value = 'This card has already been added.'
        codeInputRef.value?.$el.select()
        return
    }

    // Total up the current payment amount to be made by gift cards
    const giftCardsPaymentTotal = computeTotalGiftCardAmount(giftCards.value)

    // If the current payment from gift cards already covers the checkout total, return
    if (giftCardsPaymentTotal.isGreaterThanOrEqualTo(props.checkoutTotal)) {
        errorMessage.value =
            'Your current gift cards already cover your total for today.'
        codeInputRef.value.$el.select()
        return
    }

    // Calculate balance to use
    let balanceToUse = props.checkoutTotal.minus(giftCardsPaymentTotal)
    if (balanceToUse.isGreaterThan(giftCardCurrentBalance)) {
        balanceToUse = giftCardCurrentBalance
    }

    // Add the gift card to the gift cards to use
    giftCards.value = [
        ...giftCards.value,
        {
            currentBalance: data.giftCard.currentBalance,
            amount: balanceToUse.toFixed(2),
            paymentSourceId: props.paymentSourceId,
            token: data.giftCard.code
        }
    ]
    giftCardInputValue.value = ''
    showCodeInput.value = false
    errorMessage.value = ''
    toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Gift Card Added',
        life: 3000,
        closable: false
    })
}

const showCodeInput = ref(false)
async function handleToggleInput() {
    // Reset value
    giftCardInputValue.value = ''

    // If open, close
    if (showCodeInput.value) {
        showCodeInput.value = false
    }
    // Open and focus
    else {
        errorMessage.value = ''
        showCodeInput.value = true
        await nextTick()
        codeInputRef.value.$el.focus()
    }
}

function handleRemoveGiftCard(giftCardIndex: number) {
    giftCards.value = calibrateGiftCardTotals(
        [
            ...giftCards.value.slice(0, giftCardIndex),
            ...giftCards.value.slice(giftCardIndex + 1)
        ],
        props.checkoutTotal
    )
}

// When the total in the checkout changes (e.g. delete an item), calibrate the gift cards
watch(
    () => props.checkoutTotal,
    newCheckoutTotal => {
        giftCards.value = calibrateGiftCardTotals(
            giftCards.value,
            newCheckoutTotal
        )
    }
)

// Ensure that we use all of gift card amounts that we can
function calibrateGiftCardTotals(
    giftCards: GiftCardPaymentInfo[],
    newCheckoutTotal: Decimal
): GiftCardPaymentInfo[] {
    const newGiftCards = [
        ...giftCards.map(card => {
            return {
                ...card
            }
        })
    ]

    // Remove gift cards that exceed the checkout total - no longer needed
    while (needToRemoveGiftCard(giftCards, newCheckoutTotal)) {
        giftCards.pop()
    }

    // If we still have gift cards, need to double check balance to use totals
    if (newGiftCards.length > 0) {
        // Adjust the balance on the card at the end
        const lastCard = newGiftCards.pop()
        if (lastCard === undefined) {
            throw new Error()
        }

        const otherCardsTotal = computeTotalGiftCardAmount(newGiftCards)
        const remainingTotal = props.checkoutTotal.minus(otherCardsTotal)
        if (
            remainingTotal.isGreaterThan(new Decimal(lastCard.currentBalance))
        ) {
            lastCard.amount = lastCard.currentBalance
        } else {
            lastCard.amount = remainingTotal.toFixed(2)
        }
        newGiftCards.push(lastCard)
    }

    return newGiftCards
}

function needToRemoveGiftCard(
    giftCards: GiftCardPaymentInfo[],
    checkoutTotal: Decimal
): boolean {
    // If we removed the last gift card, would we still have enough to cover the checkout
    const withLastCardRemoved = giftCards.slice(0, giftCards.length - 1)
    return computeTotalGiftCardAmount(
        withLastCardRemoved
    ).isGreaterThanOrEqualTo(checkoutTotal)
}

function computeTotalGiftCardAmount(giftCards: GiftCardPaymentInfo[]) {
    let amountTotal = Decimal.zero()
    giftCards.forEach(card => {
        amountTotal = amountTotal.plus(new Decimal(card.amount))
    })
    return amountTotal
}

// Update the global tracker of the gift card amounts so we can show in the cart
// on the right side of the checkout
const updateGiftCards = useUpdateCheckoutGiftCardData()
watch(
    giftCards,
    newValue => {
        updateGiftCards(newValue)
    },
    {
        deep: true
    }
)
</script>
